// ── ResponseTypePicker — the 5-card response-type chooser (SS2B-2b, Phase 2) ──
//
// FIVE CARDS, not a select (spec card 1): the choice determines which fields become required, and a
// dropdown would hide that. Each card carries its glyph, name, and one-line consequence. The grid
// reflows to two columns then one on narrow widths. The hue mirrors the type chip (RR_TYPE_SPECS).

import { RR_TYPE_SPECS } from './rrPresentation';
import type { RiskResponseType } from '@/api/types/risk-acceptance.types';

const CARDS: { key: RiskResponseType; consequence: string }[] = [
  { key: 'accept',   consequence: 'Live with the risk for a stated period' },
  { key: 'avoid',    consequence: 'Stop using the exposed scope' },
  { key: 'mitigate', consequence: 'Reduce it with compensating controls' },
  { key: 'share',    consequence: 'Carry it jointly with a third party' },
  { key: 'transfer', consequence: 'Move it wholly to a third party' },
];

interface ResponseTypePickerProps {
  value: RiskResponseType;
  onChange: (next: RiskResponseType) => void;
  disabled?: boolean;
}

export function ResponseTypePicker({ value, onChange, disabled }: ResponseTypePickerProps) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label="Response type">
      {CARDS.map(({ key, consequence }) => {
        const spec = RR_TYPE_SPECS[key];
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(key)}
            className={`flex flex-col gap-1.5 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              selected
                ? 'border-accent-cyan bg-accent-cyan-dim/15'
                : 'border-border-subtle bg-bg-surface hover:border-accent-cyan/40'
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold"
                style={{ color: spec.hue, borderColor: spec.hue, borderWidth: 1 }}
              >
                {spec.glyph}
              </span>
              <span className="text-sm font-semibold text-text-primary">{spec.name}</span>
            </div>
            <span className="text-xs leading-snug text-text-secondary">{consequence}</span>
          </button>
        );
      })}
    </div>
  );
}
